import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router';
import { useQuery } from 'react-query';
import { ROUTE_PARAMS } from '../../../utilities/navigation';
import { Edit, Delete, Cancel, Save } from '@mui/icons-material';
import { Stack, Button, Typography, IconButton, Skeleton, Dialog, DialogTitle, DialogContent, DialogContentText, TextField, Select, MenuItem, DialogActions } from '@mui/material';
import { useHelpCenterNavigation } from '../../../hooks/help/useHelpCenterNavigation';
import { EAudience, HelpArticle } from '../../../types/db';
import { useHelpDocs, useHelpInfo } from '../../../hooks/help/useHelpDocs';
import { StorageHelpFEService } from '../../../services/storage';
import { MarkdownEditor } from '../../../components/markdown';
import { getArticleContent } from '../../../services/help';

/** Manage the articles in the help center */
export const HelpManageArticlesPage = () => {
    const { [ROUTE_PARAMS.articleID]: articleId } = useParams();
    if (!articleId) {
        //general article page
        return <HelpManageArticlesContainer />;
    }
    return <HelpManageArticleContainer articleId={articleId} />;
};


const HelpManageArticlesContainer = () => {
    const { navigateToManage } = useHelpCenterNavigation();
    const { docs: articles, isLoading, create, delete: deleteArticle } = useHelpDocs('article');
    const [addDialogOpen, setAddDialogOpen] = useState(false);

    const handleAddArticle = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const formJson = Object.fromEntries((formData as any).entries());
        const title = formJson.title;
        const description = formJson.description || undefined;
        const audience = formJson.audience || EAudience.COMMON;
        const parent_id = formJson.parent || null;
        create({
            title,
            description,
            audience,
            parent_id,
        }).then((articleId) => {
            setAddDialogOpen(false);
            navigateToManage("article", articleId)
        })
    }

    return <Stack direction='column'>
        <Button onClick={() => setAddDialogOpen(true)}>Add article</Button>
        {
            !isLoading ?
                articles.map(article =>
                    <Stack key={article.id} direction={'row'} spacing={2} justifyContent={'space-between'} alignItems={'start'}>
                        <Typography>{article.title}</Typography>
                        <Typography flex={1}>{article.description || ''}</Typography>
                        <IconButton onClick={() => navigateToManage("article", article.id)}>
                            <Edit />
                        </IconButton>
                        <IconButton onClick={() => deleteArticle(article.id)}>
                            <Delete />
                        </IconButton>
                    </Stack>
                )
                :
                <Skeleton variant='rectangular' width={'100%'} height={'100px'} />
        }
        <Dialog open={addDialogOpen}
            onClose={() => setAddDialogOpen(false)}>
            <DialogTitle>Add article</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Add Article
                </DialogContentText>
                <form onSubmit={handleAddArticle} id="form">
                    <TextField
                        autoFocus
                        required
                        margin="dense"
                        name="title"
                        label="Title"
                        type="text"
                        fullWidth
                        variant="standard"
                    />
                    <TextField
                        required
                        margin="dense"
                        name="description"
                        label="Descrition"
                        type="text"
                        fullWidth
                        variant="standard"
                        multiline
                    />
                    <Select
                        name='audience'
                        label='Audience'
                        variant='standard'
                        fullWidth
                        defaultValue={EAudience.COMMON}
                    >
                        <MenuItem value={EAudience.COMMON}>Common</MenuItem>
                        <MenuItem value={EAudience.HOST}>Host</MenuItem>
                        <MenuItem value={EAudience.COHOST}>CoHost</MenuItem>
                        <MenuItem value={EAudience.GUEST}>Guest</MenuItem>
                    </Select>
                    <Select
                        name='parent'
                        label='Parent Article'
                        variant='standard'
                        fullWidth
                        defaultValue={''}
                    >
                        <MenuItem key={'none'} value={''}>None</MenuItem>
                        {
                            articles.map(article =>
                                <MenuItem key={article.id} value={article.id}>{article.title}</MenuItem>
                            )}
                    </Select>
                </form>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
                <Button type="submit" form="form">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    </Stack>
}

/** View a article in edit mode (being able to edit and delete) */
const HelpManageArticleContainer = ({ articleId }: { articleId: string }) => {
    const { navigateToManage } = useHelpCenterNavigation();
    const { info: article, isLoading, isError, update, delete: deleteIt } = useHelpInfo<HelpArticle>(articleId);
    const [localArticle, setLocalArticle] = useState(article);
    useEffect(() => {
        if (!article) return;
        setLocalArticle(article)
    }, [article]);

    const { data: content, isLoading: isLoadingContent } = useQuery(["HelpArticleManage", articleId],
        () => getArticleContent(articleId, "en"),
        { refetchOnWindowFocus: false, retry: false }
    )
    const [markdown, setMarkdown] = useState<string>('');
    useEffect(() => {
        if (!content) return;
        setMarkdown(content)
    }, [content])

    const handleSave = useCallback(async () => {
        if (!!localArticle) {
            await update(localArticle)
        }
        if (!!markdown) {
            await StorageHelpFEService(articleId).uploadMarkdown(markdown)
        }
        navigateToManage("article")
    }, [articleId, localArticle, markdown, navigateToManage, update])

    if (isError || !article || !localArticle) {
        return null;
    }

    return <Stack direction='column'>
        {
            !isLoading ?
                <Stack direction={'column'} spacing={2}>
                    <TextField
                        label="title"
                        value={localArticle.title || ''}
                        onChange={(e) => setLocalArticle(lt => ({ ...lt!, title: e.target.value }))}
                        required
                    />
                    <TextField
                        label="description"
                        value={localArticle.description || ''}
                        onChange={(e) => setLocalArticle(lt => ({ ...lt!, description: e.target.value }))}
                    />
                    {!isLoadingContent ?
                        <MarkdownEditor
                            markdown={markdown}
                            onMarkdownChange={setMarkdown}
                        />
                        : <Skeleton variant='rectangular' width={'100%'} height={'200px'} />}
                    <Stack direction={'row'} justifyContent={'space-evenly'} alignItems={'center'}>
                        <IconButton onClick={() => navigateToManage("article")}>
                            <Cancel />
                        </IconButton>
                        <IconButton onClick={handleSave}>
                            <Save />
                        </IconButton>
                        <IconButton onClick={() => deleteIt().then(() => { navigateToManage("article") })}>
                            <Delete />
                        </IconButton>
                    </Stack>
                </Stack>
                :
                <Skeleton variant='rectangular' width={'100%'} height={'100px'} />
        }
    </Stack>
}